import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { request } from './helper';

import { User } from "@/types"

interface AmountReport {
  [key: string]: any;
}

interface UsersState {
  users: User[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  reportStatus: 'idle' | 'loading' | 'success' | 'failed';
  supportRequestStatus: 'idle' | 'loading' | 'success' | 'failed' | null;
  createAgencyStatus: 'idle' | 'loading' | 'success' | 'failed' | null;
  amountReport: AmountReport | null;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// **Initial State**
const initialState: UsersState = {
  users: [],
  status: 'idle',
  reportStatus: 'idle',
  supportRequestStatus: null,
  createAgencyStatus: null,
  amountReport: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  },
};

// **Thunks**
export const usersGetAll = createAsyncThunk(
  'users/getAll',
  async ({ page = 1, perPage = 50, search }: { page?: number; perPage?: number; search?: string }, { rejectWithValue }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      perPage: perPage.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    const response = await request(`admin/user/all?${params.toString()}`);
    if (response.success) {
      return {
        data: response.data,
        pagination: response.pagination,
      };
    }
    return rejectWithValue(response.error);
  }
);

export const usersDeleteUser = createAsyncThunk<
  number, // Return type (ID of the deleted user)
  number // Argument type (ID of the user to delete)
>('users/deleteUser', async (id, { rejectWithValue }) => {
  const response = await request(`admin/user/${id}`, 'DELETE');
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to delete user');
});

export const usersUpdateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, data }: { id: number; data: Partial<User> }, { rejectWithValue }) => {
    const response = await request(`admin/user/${id}`, 'PUT', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

export const usersCreateAgencyForUser = createAsyncThunk(
  'users/createAgencyForUser',
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    const response = await request(`admin/user/agency/${id}`, 'POST', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

export const usersGetAmountReport = createAsyncThunk(
  'users/getAmountReport',
  async ({ id, date }: { id: number; date: any }, { rejectWithValue }) => {
    const response = await request(`admin/user/amount-report/${id}`, 'POST', {date});
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

export const sendSupportRequest = createAsyncThunk(
  'users/sendSupportRequest',
  async (data: any, { rejectWithValue }) => {
    const response = await request(`api/debug/support`, 'POST', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

export const usersUpdateUserNotAdmin = createAsyncThunk(
  'users/updateUserNotAdmin',
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    const response = await request(`api/user/${id}`, 'PUT', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

// **Slice**
export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Helpers for loading and failed states
    const setLoading = (state: UsersState) => {
      state.status = 'loading';
    };
    const setFailed = (state: UsersState) => {
      state.status = 'failed';
    };

    // **Get All Users**
    builder
      .addCase(usersGetAll.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(usersGetAll.fulfilled, (state, action) => {
        state.users = action.payload.data || [];
        state.status = 'success';
        state.pagination = {
          current_page: action.payload.pagination.current_page,
          last_page: action.payload.pagination.last_page,
          per_page: action.payload.pagination.per_page,
          total: action.payload.pagination.total,
        };
      })
      .addCase(usersGetAll.rejected, (state) => {
        state.status = 'failed';
      });

    // **Delete User**
    builder
      .addCase(usersDeleteUser.pending, setLoading)
      .addCase(usersDeleteUser.fulfilled, (state, { payload }: PayloadAction<number>) => {
        state.status = 'success';
        state.users = state.users.filter((user) => user.id !== payload);
      })
      .addCase(usersDeleteUser.rejected, setFailed);

    // **Update User**
    builder
      // .addCase(usersUpdateUser.pending, setLoading)
      .addCase(usersUpdateUser.fulfilled, (state, { payload }: PayloadAction<User>) => {
        // state.status = 'success';
        state.users = state.users.map((user) =>
          user.id === payload.id ? { ...user, ...payload } : user
        );
      })
      // .addCase(usersUpdateUser.rejected, setFailed);

    // **Create Agency for User**
    builder
      .addCase(usersCreateAgencyForUser.pending, (state) => {
        state.createAgencyStatus = 'loading';
      })
      .addCase(usersCreateAgencyForUser.fulfilled, (state, { payload }: PayloadAction<User>) => {
        state.createAgencyStatus = 'success';
        state.users = state.users.map((user) =>
          user.id === payload.id ? { ...user, ...payload } : user
        );
      })
      .addCase(usersCreateAgencyForUser.rejected, (state) => {
        state.createAgencyStatus = 'failed';
      });

    // **Get Amount Report**
    builder
      .addCase(usersGetAmountReport.pending, (state) => {
        state.reportStatus = 'loading';
      })
      .addCase(usersGetAmountReport.fulfilled, (state, { payload }: PayloadAction<AmountReport>) => {
        state.reportStatus = 'success';
        state.amountReport = payload;
      })
      .addCase(usersGetAmountReport.rejected, (state) => {
        state.reportStatus = 'failed';
      });

    // **Send Support Request**
    builder
      .addCase(sendSupportRequest.pending, (state) => {
        state.supportRequestStatus = 'loading';
      })
      .addCase(sendSupportRequest.fulfilled, (state) => {
        state.supportRequestStatus = 'success';
      })
      .addCase(sendSupportRequest.rejected, (state) => {
        state.supportRequestStatus = 'failed';
      });

    // **Update User Not Admin**
    builder
      .addCase(usersUpdateUserNotAdmin.pending, setLoading)
      .addCase(usersUpdateUserNotAdmin.fulfilled, (state, { payload }: PayloadAction<User>) => {
        state.status = 'success';
        state.users = state.users.map((u) => (u.id === payload.id ? { ...u, ...payload } : u));
        // Optionally update pagination user if not in list
      })
      .addCase(usersUpdateUserNotAdmin.rejected, setFailed);
  },
});

// **Export Reducer**
export default usersSlice.reducer;